import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Grid, Pagination, CircularProgress, Stack, Button, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import VideoCard from './VideoCard';
import VideoFilters from './VideoFilters';
import UploadVideoModal from './UploadVideoModal';
import ConfirmDialog from '../../components/base/ConfirmDialog'; // ✅ Import du dialogue

const Videos = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIntervenant, setSelectedIntervenant] = useState(''); 
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState(null);
  
  // ✅ États pour ConfirmDialog
  const [openConfirm, setOpenConfirm] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);

  const rowsPerPage = 12;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const rolePath = Number(user?.role) === 3 ? 'intervenant' : 'admin';
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/${rolePath}/streaming/public/videos/g/l/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(res.data.data || []);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => { if (user && token) fetchData(); }, [fetchData, user, token]);

  const intervenantsList = useMemo(() => {
    const map = new Map();
    videos.forEach(v => {
      if (v.user && !map.has(v.user.id_user)) {
        map.set(v.user.id_user, v.user);
      }
    });
    return Array.from(map.values());
  }, [videos]);

  const filteredVideos = useMemo(() => {
    return videos.filter(v => {
      const searchTerm = search.toLowerCase();
      const matchTitle = (v.name_video_lbrv || '').toLowerCase().includes(searchTerm);
      const authorFullName = `${v.user?.prenom_user} ${v.user?.nom_user}`.toLowerCase();
      const matchAuthor = authorFullName.includes(searchTerm);
      const matchIntervenantSelect = selectedIntervenant === '' || v.user?.id_user === selectedIntervenant;
      return (matchTitle || matchAuthor) && matchIntervenantSelect;
    });
  }, [videos, search, selectedIntervenant]);

  const currentVideos = filteredVideos.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // ✅ 1. Prépare la suppression (Ouvre le dialogue)
  const handleDeleteClick = (id) => {
    setVideoToDelete(id);
    setOpenConfirm(true);
  };

  // ✅ 2. Exécute la suppression après confirmation
  const handleConfirmDelete = async () => {
    try {
      const rolePath = Number(user?.role) === 3 ? 'intervenant' : 'admin';
      await axios.delete(`${process.env.REACT_APP_API_URL}/${rolePath}/streaming/public/videos/d/delete/${videoToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vidéo supprimée");
      setOpenConfirm(false); // Ferme le dialogue
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleEdit = (video) => {
    setVideoToEdit(video);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setVideoToEdit(null);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <CircularProgress sx={{ color: '#4318FF' }} />
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, bgcolor: '#F4F7FE', minHeight: '100vh' }}>
      
      <VideoFilters 
        onSearchChange={setSearch} 
        onIntervenantChange={setSelectedIntervenant}
        selectedIntervenant={selectedIntervenant}
        intervenants={intervenantsList}
      >
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={() => setOpenModal(true)}
          sx={{ bgcolor: '#4318FF', borderRadius: '16px', fontWeight: 700, px: 3, py: 1.5, textTransform: 'none' }}
        >
          Publier
        </Button>
      </VideoFilters>

      <Grid container spacing={4}>
        {currentVideos.length > 0 ? (
          currentVideos.map((video) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={video.id_video_lbrv}>
              <VideoCard 
                video={video} 
                onUpdate={handleEdit} 
                onDelete={handleDeleteClick} // ✅ Passe la nouvelle fonction
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 10, color: '#A3AED0' }}>
              <Typography variant="h6">Aucune vidéo trouvée</Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {filteredVideos.length > rowsPerPage && (
        <Stack alignItems="center" sx={{ mt: 8 }}>
          <Pagination 
            count={Math.ceil(filteredVideos.length / rowsPerPage)} 
            page={page} 
            onChange={(e, v) => setPage(v)}
            color="primary"
          />
        </Stack>
      )}

      {/* MODAL UPLOAD/EDIT */}
      <UploadVideoModal 
        open={openModal} 
        onClose={handleCloseModal} 
        onRefresh={fetchData} 
        user={user}
        videoToEdit={videoToEdit} 
      />

      {/* ✅ DIALOGUE DE CONFIRMATION PERSONNALISÉ */}
      <ConfirmDialog
        open={openConfirm}
        title="Supprimer la vidéo"
        message="Voulez-vous vraiment supprimer cette vidéo ? Cette action est irréversible."
        onConfirm={handleConfirmDelete}
        onCancel={() => setOpenConfirm(false)}
      />
    </Box>
  );
};

export default Videos;
